// src/contexts/AuthContext.jsx
import React, { createContext, useContext, useState, useEffect } from 'react'
import { supabase, signInWithGoogle, signOut, getUserProfile, createUserProfile } from '../lib/supabase'

const AuthContext = createContext()
// Firebase mode: Firebase Auth with Google sign-in only (loaded lazily so other modes skip the SDK).
const USE_FIREBASE = process.env.REACT_APP_DB_PROVIDER === 'firebase'
const loadFirebase = () => import('../lib/firebase')
const localIdentity = email => {
  const key = email.trim().toLowerCase()
  const identities = JSON.parse(localStorage.getItem('spcc:local-identities') || '{}')
  const saved = JSON.parse(localStorage.getItem('spcc:auth:user') || 'null')
  if (!identities[key]) identities[key] = saved?.email?.toLowerCase() === key ? saved.id : crypto.randomUUID()
  localStorage.setItem('spcc:local-identities', JSON.stringify(identities))
  return identities[key]
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [isSupabaseEnabled, setIsSupabaseEnabled] = useState(false)

  // Check if Supabase is configured AND we're not in local mode
  useEffect(() => {
    const dbProvider = process.env.REACT_APP_DB_PROVIDER || 'supabase'
    if (dbProvider === 'local' || dbProvider === 'firebase') {
      // Local mode: bypass Supabase auth entirely, use localStorage fallback
      setIsSupabaseEnabled(false)
      return
    }
    const supabaseUrl = process.env.REACT_APP_SUPABASE_URL
    const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY
    setIsSupabaseEnabled(!!(supabaseUrl && supabaseKey))
  }, [])

  // Firebase auth state
  useEffect(() => {
    if (!USE_FIREBASE) return
    let unsubscribe = () => {}
    let cancelled = false
    loadFirebase().then(({ onFirebaseAuthChange, toAppUser }) => {
      if (cancelled) return
      unsubscribe = onFirebaseAuthChange(fbUser => {
        const appUser = toAppUser(fbUser)
        setUser(appUser)
        setProfile(appUser)
        setLoading(false)
      })
    }).catch(error => {
      console.error('Error initializing Firebase auth:', error)
      setLoading(false)
    })
    return () => { cancelled = true; unsubscribe() }
  }, [])

  // Initialize auth state
  useEffect(() => {
    if (USE_FIREBASE) return
    const initializeAuth = async () => {
      setLoading(true)

      if (isSupabaseEnabled) {
        // Use Supabase authentication
        try {
          const { data: { session }, error } = await supabase.auth.getSession()

          if (error) {
            console.error('Error getting session:', error)
          } else if (session?.user) {
            await handleSupabaseUser(session.user)
          }
        } catch (error) {
          console.error('Error initializing Supabase auth:', error)
        }

        // Listen for auth changes
        const { data: { subscription } } = supabase.auth.onAuthStateChange(
          async (event, session) => {
            if (event === 'SIGNED_IN' && session?.user) {
              await handleSupabaseUser(session.user)
            } else if (event === 'SIGNED_OUT') {
              setUser(null)
              setProfile(null)
            }
          }
        )

        setLoading(false)
        return () => subscription.unsubscribe()
      } else {
        // Fallback to localStorage authentication for development
        const savedUser = localStorage.getItem('spcc:auth:user')
        if (savedUser) {
          try {
            const userData = JSON.parse(savedUser)
            setUser(userData)
            setProfile(userData)
          } catch (error) {
            console.error('Failed to parse saved user data:', error)
            localStorage.removeItem('spcc:auth:user')
          }
        }
        setLoading(false)
      }
    }

    initializeAuth()
  }, [isSupabaseEnabled])

  const handleSupabaseUser = async (supabaseUser) => {
    try {
      setUser(supabaseUser)

      // Get or create user profile
      let { data: userProfile, error } = await getUserProfile(supabaseUser.id)

      if (error || !userProfile) {
        // Create profile if it doesn't exist
        const { data: newProfile, error: createError } = await createUserProfile(supabaseUser)
        if (createError) {
          console.error('Error creating user profile:', createError)
        } else {
          userProfile = newProfile
        }
      }

      setProfile(userProfile)

      // Note: Data migration can be done separately if needed

    } catch (error) {
      console.error('Error handling Supabase user:', error)
    }
  }

  // Authentication methods
  const loginWithGoogle = async () => {
    if (USE_FIREBASE) {
      const { signInWithGoogle: firebaseGoogleSignIn } = await loadFirebase()
      await firebaseGoogleSignIn()
      // onAuthStateChanged sets the user
      return
    }
    if (!isSupabaseEnabled) {
      throw new Error('Supabase not configured. Please set up environment variables.')
    }

    try {
      const { error } = await signInWithGoogle()
      if (error) {
        throw error
      }
      // The actual user setting will be handled by the auth state change listener
    } catch (error) {
      console.error('Error signing in with Google:', error)
      throw error
    }
  }

  const loginWithEmail = async (email, password) => {
    if (USE_FIREBASE) throw new Error('Use "Continue with Google" to sign in.')
    if (isSupabaseEnabled) {
      try {
        const { data, error } = await supabase.auth.signInWithPassword({
          email,
          password
        })

        if (error) {
          throw error
        }

        return data.user
      } catch (error) {
        console.error('Error signing in with email:', error)
        throw error
      }
    } else {
      // Fallback to demo authentication
      if (!email || !password) {
        throw new Error('Email and password are required')
      }

      const userData = {
        id: localIdentity(email),
        email: email,
        name: email.split('@')[0],
        loginTime: new Date().toISOString(),
      }

      setUser(userData)
      setProfile(userData)
      localStorage.setItem('spcc:auth:user', JSON.stringify(userData))

      return userData
    }
  }

  const register = async (email, password, name) => {
    if (USE_FIREBASE) throw new Error('Use "Continue with Google" to sign in.')
    if (isSupabaseEnabled) {
      try {
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: {
              full_name: name || email.split('@')[0]
            }
          }
        })

        if (error) {
          throw error
        }

        return data.user
      } catch (error) {
        console.error('Error registering user:', error)
        throw error
      }
    } else {
      // Fallback to demo registration
      if (!email || !password) {
        throw new Error('Email and password are required')
      }

      const userData = {
        id: localIdentity(email),
        email: email,
        name: name || email.split('@')[0],
        loginTime: new Date().toISOString(),
        registeredAt: new Date().toISOString(),
      }

      setUser(userData)
      setProfile(userData)
      localStorage.setItem('spcc:auth:user', JSON.stringify(userData))

      return userData
    }
  }

  const logout = async () => {
    const { clearGoogleCalendarSession } = await import('../services/googleCalendar')
    clearGoogleCalendarSession()
    try {
      const { clearOutlookSession } = await import('../services/outlookCalendar')
      await clearOutlookSession()
    } catch {
      // A Microsoft/network failure must not prevent signing out of SPCC.
      console.warn('Could not fully clear the Outlook session cache.')
    }
    if (USE_FIREBASE) {
      const { signOutFirebase } = await loadFirebase()
      await signOutFirebase()
    } else if (isSupabaseEnabled) {
      try {
        const { error } = await signOut()
        if (error) {
          console.error('Error signing out:', error)
        }
      } catch (error) {
        console.error('Error during logout:', error)
      }
    } else {
      // Fallback logout
      setUser(null)
      setProfile(null)
      localStorage.removeItem('spcc:auth:user')
      // Clear user-specific data on logout
      const keys = Object.keys(localStorage)
      keys.forEach(key => {
        if (key.startsWith(`spcc:user:${user?.id}:`)) {
          localStorage.removeItem(key)
        }
      })
    }
  }

  const updateProfile = async (updates) => {
    if (USE_FIREBASE) {
      // Profile edits stay in memory; identity comes from the Google account.
      const updatedProfile = { ...profile, ...updates }
      setProfile(updatedProfile)
      return updatedProfile
    }
    if (isSupabaseEnabled && user?.id) {
      try {
        const { data, error } = await supabase
          .from('profiles')
          .update(updates)
          .eq('id', user.id)
          .select()
          .single()

        if (error) {
          throw error
        }

        setProfile(data)
        return data
      } catch (error) {
        console.error('Error updating profile:', error)
        throw error
      }
    } else {
      // Fallback profile update
      const updatedProfile = { ...profile, ...updates }
      setProfile(updatedProfile)
      localStorage.setItem('spcc:auth:user', JSON.stringify(updatedProfile))
      return updatedProfile
    }
  }

  const value = {
    user,
    profile,
    loading,
    isAuthenticated: !!user,
    isSupabaseEnabled,
    isFirebaseEnabled: USE_FIREBASE,
    googleOnly: USE_FIREBASE,
    loginWithGoogle,
    loginWithEmail,
    register,
    logout,
    updateProfile,
    // Legacy method names for backward compatibility
    login: loginWithEmail
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export default AuthContext
